import React, { useEffect, useRef, useState } from 'react';
import { Severity, SEVERITY_TEXT, Thresholds, severityFromThresholds } from './severity';
import { usePrefersReducedMotion } from '@/hooks/useMediaQuery';

interface MetricValueProps extends React.HTMLAttributes<HTMLSpanElement> {
  value: string | number;
  /** Explicit severity. Ignored if `thresholds` is provided with a numeric value. */
  severity?: Severity;
  /** Derive severity from a numeric value. Takes precedence over `severity`. */
  thresholds?: Thresholds;
  /**
   * 'hero' renders the instrument-panel treatment: a large heading-face
   * tabular numeral with the optional `unit` set small and muted beside it.
   * Default 'inline' preserves the original compact rendering.
   */
  size?: 'inline' | 'hero';
  /** Short unit/context label ("%", "on duty", "of 200") shown after the value. */
  unit?: string;
  /**
   * De-emphasized rendering for operationally quiet values (e.g. zero-value
   * KPIs): value renders `text-theme-tertiary` instead of the severity color.
   * Ignored when the resolved severity is not 'calm'.
   */
  muted?: boolean;
}

/** A number / ID / timestamp rendered with tabular figures and optional threshold color. */
export default function MetricValue({
  value,
  severity = 'calm',
  thresholds,
  size = 'inline',
  unit,
  muted = false,
  className = '',
  style,
  ...rest
}: MetricValueProps) {
  const prefersReducedMotion = usePrefersReducedMotion();
  // Change tick: when a live NUMERIC value changes (not on mount), flash a
  // brief severity-tinted highlight — "information arriving" per the motion
  // doctrine. The counter keys the span so rapid successive changes remount
  // it and deterministically restart the CSS animation.
  const prevValueRef = useRef(value);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const prev = prevValueRef.current;
    prevValueRef.current = value;
    if (prefersReducedMotion) return;
    if (typeof value !== 'number' || typeof prev !== 'number') return;
    if (prev === value) return;
    setTick((t) => t + 1);
  }, [value, prefersReducedMotion]);

  const resolved: Severity =
    thresholds && typeof value === 'number' ? severityFromThresholds(value, thresholds) : severity;
  // Hero keeps the heading face (large tabular Outfit numeral per spec W2);
  // inline values render in the instrument mono face.
  // Hero numerals scale up again at wall-display widths (3xl = 1920px, 4xl = 2560px)
  // so KPI values stay legible from across an EOC.
  const sizeClass =
    size === 'hero'
      ? 'font-heading text-3xl md:text-4xl 3xl:text-5xl 4xl:text-6xl font-bold leading-none'
      : 'font-mono';
  const textClass = muted && resolved === 'calm' ? 'text-theme-tertiary' : SEVERITY_TEXT[resolved];
  const tickClass = tick > 0 ? 'metric-tick' : '';
  // Non-calm severities tint the tick with their own bg token; calm uses the
  // neutral fallback baked into the keyframe.
  const tickStyle: React.CSSProperties | undefined =
    tick > 0 && resolved !== 'calm'
      ? ({ '--metric-tick-bg': `var(--severity-${resolved}-bg)` } as React.CSSProperties)
      : undefined;

  return (
    <span
      key={tick}
      className={`tabular-nums ${sizeClass} ${textClass} ${tickClass} ${className}`}
      style={tickStyle || style ? { ...style, ...tickStyle } : undefined}
      {...rest}
    >
      {value}
      {unit && (
        <span className="ml-1.5 text-xs 3xl:text-sm font-medium text-theme-tertiary align-baseline font-sans tracking-wide">
          {unit}
        </span>
      )}
    </span>
  );
}
